import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';
import {
  LayoutDashboard, Users, CreditCard, MessageSquare, Trophy, User, FileText,
  HelpCircle, Mail, Settings, LogOut, Menu, X, ChevronRight, Plus, Pencil,
  Trash2, Check, Eye, EyeOff, BarChart3, Download, QrCode, Atom, Image, Key, Shield, Calendar, Phone, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

// Safe storage for Safari private mode
var safeStorage = {
  getItem: function(key) { try { return localStorage.getItem(key); } catch(e) { return null; } }
};

var getAuthHeader = function() {
  return { headers: { Authorization: 'Bearer ' + safeStorage.getItem('adminToken') } };
};

// Sidebar Navigation
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/students', icon: Users, label: 'Registered Students' },
    { path: '/admin/complimentary-sessions', icon: Calendar, label: 'Complimentary Sessions' },
    { path: '/admin/payment-verifications', icon: CreditCard, label: 'Payment Verifications' },
    { path: '/admin/payment-settings', icon: Key, label: 'Payment Settings' },
    { path: '/admin/courses', icon: FileText, label: 'Courses' },
    { path: '/admin/testimonials', icon: MessageSquare, label: 'Testimonials' },
    { path: '/admin/results', icon: Trophy, label: 'Results' },
    { path: '/admin/founder', icon: User, label: 'Founder' },
    { path: '/admin/blogs', icon: FileText, label: 'Blog' },
    { path: '/admin/faqs', icon: HelpCircle, label: 'FAQs' },
    { path: '/admin/sections', icon: Settings, label: 'Site Sections' },
    { path: '/admin/pages', icon: FileText, label: 'Pages' },
    { path: '/admin/newsletter', icon: Mail, label: 'Newsletter' },
    { path: '/admin/contacts', icon: Mail, label: 'Contact Forms' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setIsOpen(false)} />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-deep-navy border-r border-surface-navy transform transition-transform lg:transform-none ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-surface-navy">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-electric-cyan/20 flex items-center justify-center">
                <Atom className="w-5 h-5 text-electric-cyan" />
              </div>
              <span className="font-outfit font-bold text-white">
                Physics<span className="text-electric-cyan">Co</span>
              </span>
            </Link>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-electric-cyan/20 text-electric-cyan'
                    : 'text-slate-400 hover:bg-surface-navy hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-surface-navy">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-surface-navy hover:text-white w-full transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/analytics`, getAuthHeader())
      .then(res => { setAnalytics(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <h1 className="font-outfit text-2xl font-bold text-white mb-6" data-testid="admin-dashboard-title">Dashboard</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Students', value: analytics?.total_students || 0, icon: Users },
          { label: 'Total Payments', value: analytics?.total_payments || 0, icon: CreditCard },
          { label: 'Revenue', value: `SGD ${analytics?.total_revenue?.toFixed(2) || '0.00'}`, icon: BarChart3 },
          { label: 'Currency', value: analytics?.currency || 'SGD', icon: CreditCard }
        ].map((stat, idx) => (
          <div key={idx} className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-electric-cyan/20 flex items-center justify-center">
                <stat.icon className="w-5 h-5 text-electric-cyan" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">{stat.label}</p>
                <p className="text-white font-semibold text-lg">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <h3 className="font-outfit font-semibold text-white mb-4">Students by Grade</h3>
          <div className="space-y-3">
            {Object.entries(analytics?.students_by_grade || {}).map(([grade, count]) => (
              <div key={grade} className="flex items-center gap-3">
                <span className="text-slate-400 text-sm w-32 truncate">{grade}</span>
                <div className="flex-1 h-6 bg-surface-navy rounded-full overflow-hidden">
                  <div
                    className="h-full bg-electric-cyan rounded-full"
                    style={{ width: `${(count / (analytics?.total_students || 1)) * 100}%` }}
                  />
                </div>
                <span className="text-white font-medium w-8 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6">
          <h3 className="font-outfit font-semibold text-white mb-4">Students by Subject</h3>
          <div className="space-y-3">
            {Object.entries(analytics?.students_by_subject || {}).map(([subject, count]) => (
              <div key={subject} className="flex items-center gap-3">
                <span className="text-slate-400 text-sm w-32">{subject}</span>
                <div className="flex-1 h-6 bg-surface-navy rounded-full overflow-hidden">
                  <div
                    className="h-full bg-electric-cyan rounded-full"
                    style={{ width: `${(count / (analytics?.total_students || 1)) * 100}%` }}
                  />
                </div>
                <span className="text-white font-medium w-8 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Generic CRUD Component
const CrudSection = ({ title, endpoint, fields, displayFields }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchItems = () => {
    axios.get(`${API}/admin/${endpoint}`, getAuthHeader())
      .then(res => { setItems(res.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchItems(); }, [endpoint]);

  const handleSave = async () => {
    try {
      if (editItem) {
        await axios.put(`${API}/admin/${endpoint}/${editItem.id}`, formData, getAuthHeader());
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/admin/${endpoint}`, formData, getAuthHeader());
        toast.success('Created successfully');
      }
      setDialogOpen(false);
      setEditItem(null);
      setFormData({});
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/admin/${endpoint}/${id}`, getAuthHeader());
      toast.success('Deleted successfully');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const openCreate = () => {
    setEditItem(null);
    const initial = {};
    fields.forEach(f => { initial[f.name] = f.default || ''; });
    setFormData(initial);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormData({ ...item });
    setDialogOpen(true);
  };

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-outfit text-2xl font-bold text-white">{title}</h1>
        <Button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid={`add-${endpoint}`}>
          <Plus className="w-4 h-4" /> Add New
        </Button>
      </div>

      <div className="glass-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-surface-navy">
              {displayFields.map(f => (
                <TableHead key={f.key} className="text-slate-400">{f.label}</TableHead>
              ))}
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id} className="border-surface-navy">
                {displayFields.map(f => (
                  <TableCell key={f.key} className="text-white">
                    {f.render ? f.render(item[f.key], item) : String(item[f.key] || '').slice(0, 50)}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="text-slate-400 hover:text-white">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={displayFields.length + 1} className="text-center text-slate-400 py-8">
                  No items found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-deep-navy border-surface-navy max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editItem ? 'Edit' : 'Create'} {title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {fields.map(field => (
              <div key={field.name}>
                <label className="block text-sm text-slate-400 mb-2">{field.label}</label>
                {field.type === 'textarea' ? (
                  <Textarea
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="input-dark"
                    rows={4}
                  />
                ) : field.type === 'switch' ? (
                  <Switch
                    checked={formData[field.name] || false}
                    onCheckedChange={(v) => setFormData({ ...formData, [field.name]: v })}
                  />
                ) : field.type === 'number' ? (
                  <Input
                    type="number"
                    value={formData[field.name] || 0}
                    onChange={(e) => setFormData({ ...formData, [field.name]: parseFloat(e.target.value) || 0 })}
                    className="input-dark"
                  />
                ) : field.type === 'array' ? (
                  <Textarea
                    value={Array.isArray(formData[field.name]) ? formData[field.name].join('\n') : ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value.split('\n').filter(Boolean) })}
                    className="input-dark"
                    rows={3}
                    placeholder="One item per line"
                  />
                ) : (
                  <Input
                    type={field.type || 'text'}
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="input-dark"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleSave} className="btn-primary">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Students Section
const StudentsSection = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('page', page);
      params.append('limit', 15);
      
      const res = await axios.get(`${API}/admin/students?${params}`, getAuthHeader());
      setStudents(res.data.students || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStudents(); }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchStudents();
  };

  const viewStudent = async (studentId) => {
    try {
      const res = await axios.get(`${API}/admin/students/${studentId}`, getAuthHeader());
      setSelectedStudent(res.data);
      setViewDialogOpen(true);
    } catch {
      toast.error('Failed to load student details');
    }
  };

  const updateStudentStatus = async (studentId, status) => {
    try {
      await axios.put(`${API}/admin/students/${studentId}/status?status=${status}`, {}, getAuthHeader());
      toast.success(`Student marked as ${status}`);
      fetchStudents();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const confirmDelete = (student) => {
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const deleteStudent = async () => {
    if (!studentToDelete) return;
    try {
      await axios.delete(`${API}/admin/students/${studentToDelete.id}`, getAuthHeader());
      toast.success('Student deleted successfully');
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
      fetchStudents();
    } catch {
      toast.error('Failed to delete student');
    }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/students`, getAuthHeader());
      const blob = new Blob([res.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'students.csv';
      a.click();
    } catch {
      toast.error('Export failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      disabled: 'bg-red-500/20 text-red-400',
      suspicious: 'bg-orange-500/20 text-orange-400',
      registered: 'bg-blue-500/20 text-blue-400'
    };
    return styles[status] || 'bg-slate-500/20 text-slate-400';
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-white" data-testid="admin-students-title">Student Management</h1>
          <p className="text-slate-400 text-sm mt-1">{total} total students</p>
        </div>
        <Button onClick={exportCSV} className="btn-secondary flex items-center gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="glass-card p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-dark"
              data-testid="student-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="input-dark w-full md:w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-deep-navy border-surface-navy">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
              <SelectItem value="suspicious">Suspicious</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" className="btn-primary">Search</Button>
        </form>
      </div>

      {/* Students Table */}
      {loading ? (
        <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>
      ) : (
        <div className="glass-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-surface-navy">
                <TableHead className="text-slate-400">Name</TableHead>
                <TableHead className="text-slate-400">Email</TableHead>
                <TableHead className="text-slate-400">Phone</TableHead>
                <TableHead className="text-slate-400">Country</TableHead>
                <TableHead className="text-slate-400">Course</TableHead>
                <TableHead className="text-slate-400">Registered</TableHead>
                <TableHead className="text-slate-400">Email Verified</TableHead>
                <TableHead className="text-slate-400">Payment</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-slate-400 py-8">
                    No students found
                  </TableCell>
                </TableRow>
              ) : (
                students.map(student => (
                  <TableRow key={student.id} className="border-surface-navy hover:bg-surface-navy/30">
                    <TableCell className="text-white font-medium">{student.full_name}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{student.email}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{student.phone}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{student.country || '-'}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{student.course_type}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{formatDate(student.created_at)}</TableCell>
                    <TableCell>
                      {student.email_verified ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <X className="w-4 h-4 text-red-400" />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        student.payment_status === 'paid' || student.payment_status === 'verified'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {student.payment_status || 'pending'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(student.status)}`}>
                        {student.status || 'active'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => viewStudent(student.id)}
                          className="p-1.5 text-slate-400 hover:text-electric-cyan transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => updateStudentStatus(student.id, student.status === 'suspicious' ? 'active' : 'suspicious')}
                          className="p-1.5 text-slate-400 hover:text-orange-400 transition-colors"
                          title={student.status === 'suspicious' ? 'Mark as Active' : 'Mark as Suspicious'}
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => updateStudentStatus(student.id, student.status === 'disabled' ? 'active' : 'disabled')}
                          className="p-1.5 text-slate-400 hover:text-yellow-400 transition-colors"
                          title={student.status === 'disabled' ? 'Enable Account' : 'Disable Account'}
                        >
                          {student.status === 'disabled' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => confirmDelete(student)}
                          className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                          title="Delete Student"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 p-4 border-t border-surface-navy">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary px-3 py-1 text-sm"
              >
                Previous
              </Button>
              <span className="text-slate-400 text-sm">
                Page {page} of {totalPages}
              </span>
              <Button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary px-3 py-1 text-sm"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {/* View Student Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-deep-navy border-surface-navy max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Student Details</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Full Name</p>
                  <p className="text-white">{selectedStudent.student?.full_name}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email</p>
                  <p className="text-white">{selectedStudent.student?.email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Phone</p>
                  <p className="text-white">{selectedStudent.student?.phone}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Country</p>
                  <p className="text-white">{selectedStudent.student?.country || selectedStudent.student?.country_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">School</p>
                  <p className="text-white">{selectedStudent.student?.school_name}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Grade</p>
                  <p className="text-white">{selectedStudent.student?.grade}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Course Type</p>
                  <p className="text-white">{selectedStudent.student?.course_type}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Subjects</p>
                  <p className="text-white">{selectedStudent.student?.subjects?.join(', ') || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email Verified</p>
                  <p className="text-white">{selectedStudent.student?.email_verified ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Payment Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    selectedStudent.student?.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {selectedStudent.student?.payment_status || 'pending'}
                  </span>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(selectedStudent.student?.status)}`}>
                    {selectedStudent.student?.status || 'active'}
                  </span>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Registered On</p>
                  <p className="text-white">{formatDate(selectedStudent.student?.created_at)}</p>
                </div>
              </div>
              
              {selectedStudent.student?.notes && (
                <div>
                  <p className="text-slate-400 text-sm">Notes</p>
                  <p className="text-white">{selectedStudent.student?.notes}</p>
                </div>
              )}

              {selectedStudent.payments && selectedStudent.payments.length > 0 && (
                <div>
                  <p className="text-slate-400 text-sm mb-2">Payment History</p>
                  <div className="space-y-2">
                    {selectedStudent.payments.map((payment, idx) => (
                      <div key={idx} className="bg-surface-navy/50 p-3 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-white">{payment.method}</span>
                          <span className="text-electric-cyan">SGD {payment.amount}</span>
                        </div>
                        <div className="flex justify-between text-sm text-slate-400">
                          <span>{formatDate(payment.created_at)}</span>
                          <span>{payment.payment_status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-deep-navy border-surface-navy">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Deletion</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete this student?
            </DialogDescription>
          </DialogHeader>
          {studentToDelete && (
            <div className="py-4">
              <p className="text-white font-medium">{studentToDelete.full_name}</p>
              <p className="text-slate-400 text-sm">{studentToDelete.email}</p>
              <p className="text-red-400 text-sm mt-4">
                This action cannot be undone. All student data including payment records will be permanently deleted.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDeleteDialogOpen(false)} className="btn-secondary">
              Cancel
            </Button>
            <Button onClick={deleteStudent} className="bg-red-600 hover:bg-red-700 text-white">
              Delete Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Payment Verifications Section - Manual Payment System
const PaymentVerificationsSection = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);

  const fetchPayments = async (currentPage = page, status = statusFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 15 });
      if (status && status !== 'all') params.append('status', status);
      
      const res = await axios.get(`${API}/admin/payment-verifications?${params}`, getAuthHeader());
      setPayments(res.data.payments || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(1, statusFilter); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/payment-verifications/${id}`, { payment_status: status }, getAuthHeader());
      toast.success(`Payment ${status}`);
      fetchPayments(page, statusFilter);
    } catch {
      toast.error('Update failed');
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment submission?')) return;
    try {
      await axios.delete(`${API}/admin/payment-verifications/${id}`, getAuthHeader());
      toast.success('Payment deleted');
      fetchPayments(page, statusFilter);
    } catch {
      toast.error('Delete failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-SG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      verified: 'bg-green-500/20 text-green-400',
      pending_verification: 'bg-yellow-500/20 text-yellow-400',
      rejected: 'bg-red-500/20 text-red-400'
    };
    return styles[status] || 'bg-slate-500/20 text-slate-400';
  };

  const getStatusLabel = (status) => {
    const labels = {
      verified: 'Verified',
      pending_verification: 'Pending',
      rejected: 'Rejected'
    };
    return labels[status] || status;
  };

  // Filter payments by search term
  const filteredPayments = payments.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (p.student_name && p.student_name.toLowerCase().includes(term)) ||
      (p.email && p.email.toLowerCase().includes(term)) ||
      (p.course_name && p.course_name.toLowerCase().includes(term))
    );
  });

  // Note: getImageUrl is now imported from '../utils/imageUtils'

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-white" data-testid="admin-payment-verifications-title">
            Payment Verifications
          </h1>
          <p className="text-slate-400 text-sm mt-1">{total} total submissions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by student name, email, or course..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="input-dark"
              data-testid="payments-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px] input-dark" data-testid="payments-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-deep-navy border-surface-navy">
              <SelectItem value="all" className="text-white hover:bg-surface-navy">All Statuses</SelectItem>
              <SelectItem value="pending_verification" className="text-white hover:bg-surface-navy">Pending</SelectItem>
              <SelectItem value="verified" className="text-white hover:bg-surface-navy">Verified</SelectItem>
              <SelectItem value="rejected" className="text-white hover:bg-surface-navy">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Payments Table */}
      <div className="glass-card overflow-x-auto">
        {loading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            {searchTerm ? 'No payments match your search' : 'No payment submissions yet'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-surface-navy">
                <TableHead className="text-slate-400">Submitted</TableHead>
                <TableHead className="text-slate-400">Student</TableHead>
                <TableHead className="text-slate-400">Course</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Method</TableHead>
                <TableHead className="text-slate-400">Screenshot</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map(payment => (
                <TableRow key={payment.id} className="border-surface-navy hover:bg-surface-navy/30">
                  <TableCell className="text-slate-400 text-sm">
                    {formatDate(payment.submitted_at)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-white font-medium">{payment.student_name || 'N/A'}</p>
                      <p className="text-slate-400 text-xs">{payment.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300">{payment.course_name || '-'}</TableCell>
                  <TableCell className="text-electric-cyan font-medium whitespace-nowrap">
                    {payment.currency || 'SGD'} {payment.amount}
                  </TableCell>
                  <TableCell className="text-slate-400 capitalize">
                    {payment.payment_method === 'bank_transfer' ? 'Bank Transfer' : 'PayNow'}
                  </TableCell>
                  <TableCell>
                    {payment.screenshot_path ? (
                      <div className="flex items-center gap-2">
                        <img
                          src={getImageUrl(payment.screenshot_path)}
                          alt="Screenshot"
                          className="w-10 h-10 rounded object-cover cursor-pointer hover:ring-2 hover:ring-electric-cyan/50 transition-all"
                          onClick={() => setSelectedPayment(payment)}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                          data-testid="payment-screenshot-thumbnail"
                        />
                        <button
                          onClick={() => setSelectedPayment(payment)}
                          className="text-electric-cyan hover:underline text-sm"
                        >
                          View
                        </button>
                      </div>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(payment.payment_status)}`}>
                      {getStatusLabel(payment.payment_status)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setSelectedPayment(payment)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {payment.payment_status === 'pending_verification' && (
                        <>
                          <Button 
                            size="sm" 
                            onClick={() => updateStatus(payment.id, 'verified')} 
                            className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            title="Approve"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => updateStatus(payment.id, 'rejected')} 
                            className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            title="Reject"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button 
                        size="sm" 
                        onClick={() => deletePayment(payment.id)} 
                        className="bg-slate-500/20 text-slate-400 hover:bg-slate-500/30"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPage(p => Math.max(1, p - 1)); fetchPayments(page - 1, statusFilter); }}
            disabled={page <= 1}
            className="border-surface-navy text-slate-400"
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-slate-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPage(p => Math.min(totalPages, p + 1)); fetchPayments(page + 1, statusFilter); }}
            disabled={page >= totalPages}
            className="border-surface-navy text-slate-400"
          >
            Next
          </Button>
        </div>
      )}

      {/* Payment Details Modal */}
      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="bg-deep-navy border-surface-navy max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Payment Details</DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Student Name</p>
                  <p className="text-white">{selectedPayment.student_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email</p>
                  <p className="text-white">{selectedPayment.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Course</p>
                  <p className="text-white">{selectedPayment.course_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Amount</p>
                  <p className="text-electric-cyan font-bold">{selectedPayment.currency || 'SGD'} {selectedPayment.amount}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Payment Method</p>
                  <p className="text-white capitalize">
                    {selectedPayment.payment_method === 'bank_transfer' ? 'Bank Transfer' : 'PayNow'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedPayment.payment_status)}`}>
                    {getStatusLabel(selectedPayment.payment_status)}
                  </span>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Submitted At</p>
                  <p className="text-white">{formatDate(selectedPayment.submitted_at)}</p>
                </div>
                {selectedPayment.verified_at && (
                  <div>
                    <p className="text-slate-400 text-sm">Verified At</p>
                    <p className="text-white">{formatDate(selectedPayment.verified_at)}</p>
                  </div>
                )}
              </div>
              
              {/* Screenshot Preview */}
              {selectedPayment.screenshot_path && (
                <div className="pt-4 border-t border-surface-navy">
                  <p className="text-slate-400 text-sm mb-3">Payment Screenshot</p>
                  <div className="bg-surface-navy/50 p-4 rounded-xl">
                    <img
                      src={getImageUrl(selectedPayment.screenshot_path)}
                      alt="Payment screenshot"
                      className="max-w-full max-h-[400px] object-contain mx-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(getImageUrl(selectedPayment.screenshot_path), '_blank')}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '';
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="text-center py-8"><p class="text-slate-400">Screenshot not available</p><p class="text-slate-500 text-sm mt-1">The image file may have been moved or deleted</p></div>';
                      }}
                      data-testid="payment-screenshot-preview"
                    />
                    <p className="text-slate-500 text-xs text-center mt-2">Click image to open in new tab</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              {selectedPayment.payment_status === 'pending_verification' && (
                <div className="flex gap-3 pt-4 border-t border-surface-navy">
                  <Button 
                    onClick={() => { updateStatus(selectedPayment.id, 'verified'); setSelectedPayment(null); }}
                    className="btn-primary flex-1"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve Payment
                  </Button>
                  <Button 
                    onClick={() => { updateStatus(selectedPayment.id, 'rejected'); setSelectedPayment(null); }}
                    className="bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject Payment
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPayment(null)} className="border-surface-navy text-slate-400">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Complimentary Sessions Section
const ComplimentarySessionsSection = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = async (currentPage = page, status = statusFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 15 });
      if (status && status !== 'all') params.append('status', status);
      
      const res = await axios.get(`${API}/admin/complimentary-sessions?${params}`, getAuthHeader());
      setRequests(res.data.requests || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(1, statusFilter); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/complimentary-sessions/${id}`, { status }, getAuthHeader());
      toast.success('Status updated');
      fetchRequests(page, statusFilter);
    } catch {
      toast.error('Update failed');
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      await axios.delete(`${API}/admin/complimentary-sessions/${id}`, getAuthHeader());
      toast.success('Request deleted');
      fetchRequests(page, statusFilter);
    } catch {
      toast.error('Delete failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-SG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      contacted: 'bg-green-500/20 text-green-400',
      scheduled: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-electric-cyan/20 text-electric-cyan',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return styles[status] || 'bg-slate-500/20 text-slate-400';
  };

  // Filter by search term
  const filteredRequests = requests.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (r.name && r.name.toLowerCase().includes(term)) ||
      (r.email && r.email.toLowerCase().includes(term)) ||
      (r.subject && r.subject.toLowerCase().includes(term)) ||
      (r.curriculum && r.curriculum.toLowerCase().includes(term))
    );
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-white" data-testid="admin-complimentary-title">
            Complimentary Session Requests
          </h1>
          <p className="text-slate-400 text-sm mt-1">{total} total requests</p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name, email, subject, or curriculum..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="input-dark"
              data-testid="sessions-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px] input-dark" data-testid="sessions-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-deep-navy border-surface-navy">
              <SelectItem value="all" className="text-white hover:bg-surface-navy">All Statuses</SelectItem>
              <SelectItem value="pending" className="text-white hover:bg-surface-navy">Pending</SelectItem>
              <SelectItem value="contacted" className="text-white hover:bg-surface-navy">Contacted</SelectItem>
              <SelectItem value="scheduled" className="text-white hover:bg-surface-navy">Scheduled</SelectItem>
              <SelectItem value="completed" className="text-white hover:bg-surface-navy">Completed</SelectItem>
              <SelectItem value="cancelled" className="text-white hover:bg-surface-navy">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Requests Table */}
      <div className="glass-card overflow-x-auto">
        {loading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            {searchTerm ? 'No requests match your search' : 'No complimentary session requests yet'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-surface-navy">
                <TableHead className="text-slate-400">Request Date</TableHead>
                <TableHead className="text-slate-400">Student</TableHead>
                <TableHead className="text-slate-400">Grade</TableHead>
                <TableHead className="text-slate-400">Curriculum</TableHead>
                <TableHead className="text-slate-400">Subject</TableHead>
                <TableHead className="text-slate-400">Preferred Date (SGT)</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.map(request => (
                <TableRow key={request.id} className="border-surface-navy hover:bg-surface-navy/30">
                  <TableCell className="text-slate-400 text-sm">
                    {formatDate(request.request_date)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-white font-medium">{request.name}</p>
                      <p className="text-slate-400 text-xs">{request.email}</p>
                      <p className="text-slate-500 text-xs">{request.phone_number}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300">{request.grade}</TableCell>
                  <TableCell className="text-slate-300">{request.curriculum}</TableCell>
                  <TableCell className="text-electric-cyan font-medium">{request.subject}</TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    <div>
                      <p>{request.preferred_date_sgt || request.preferred_date}</p>
                      <p className="text-slate-500 text-xs">{request.preferred_time_sgt?.split(' ')[1] || request.preferred_time_local}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                      {request.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setSelectedRequest(request)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {request.status === 'pending' && (
                        <Button 
                          size="sm" 
                          onClick={() => updateStatus(request.id, 'contacted')} 
                          className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        onClick={() => deleteRequest(request.id)} 
                        className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPage(p => Math.max(1, p - 1)); fetchRequests(page - 1, statusFilter); }}
            disabled={page <= 1}
            className="border-surface-navy text-slate-400"
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-slate-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPage(p => Math.min(totalPages, p + 1)); fetchRequests(page + 1, statusFilter); }}
            disabled={page >= totalPages}
            className="border-surface-navy text-slate-400"
          >
            Next
          </Button>
        </div>
      )}

      {/* Request Details Modal */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="bg-deep-navy border-surface-navy max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Session Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Student Name</p>
                  <p className="text-white">{selectedRequest.name}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email</p>
                  <p className="text-white">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Phone</p>
                  <p className="text-white">{selectedRequest.phone_number}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Timezone</p>
                  <p className="text-white text-sm">{selectedRequest.time_zone}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Grade</p>
                  <p className="text-white">{selectedRequest.grade}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Curriculum</p>
                  <p className="text-white">{selectedRequest.curriculum}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Subject</p>
                  <p className="text-electric-cyan font-medium">{selectedRequest.subject}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedRequest.status)}`}>
                    {selectedRequest.status}
                  </span>
                </div>
              </div>
              
              {selectedRequest.topic && (
                <div className="pt-2">
                  <p className="text-slate-400 text-sm">Requested Topic</p>
                  <p className="text-white">{selectedRequest.topic}</p>
                </div>
              )}

              <div className="pt-2 border-t border-surface-navy">
                <p className="text-slate-400 text-sm mb-2">Preferred Schedule (Singapore Time)</p>
                <div className="bg-surface-navy/50 p-3 rounded-lg flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-electric-cyan" />
                  <span className="text-white">{selectedRequest.preferred_date_sgt || selectedRequest.preferred_date}</span>
                  <Clock className="w-5 h-5 text-electric-cyan ml-4" />
                  <span className="text-white">{selectedRequest.preferred_time_sgt?.split(' ')[1] || selectedRequest.preferred_time_local}</span>
                </div>
                <p className="text-slate-500 text-xs mt-1">
                  Student's local time: {selectedRequest.preferred_date} {selectedRequest.preferred_time_local} ({selectedRequest.time_zone})
                </p>
              </div>

              {selectedRequest.message && (
                <div className="pt-2">
                  <p className="text-slate-400 text-sm">Additional Message</p>
                  <p className="text-white text-sm">{selectedRequest.message}</p>
                </div>
              )}

              <div className="pt-4 border-t border-surface-navy">
                <p className="text-slate-400 text-sm mb-2">Update Status</p>
                <Select 
                  value={selectedRequest.status} 
                  onValueChange={(v) => { updateStatus(selectedRequest.id, v); setSelectedRequest(null); }}
                >
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-deep-navy border-surface-navy">
                    <SelectItem value="pending" className="text-white hover:bg-surface-navy">Pending</SelectItem>
                    <SelectItem value="contacted" className="text-white hover:bg-surface-navy">Contacted</SelectItem>
                    <SelectItem value="scheduled" className="text-white hover:bg-surface-navy">Scheduled</SelectItem>
                    <SelectItem value="completed" className="text-white hover:bg-surface-navy">Completed</SelectItem>
                    <SelectItem value="cancelled" className="text-white hover:bg-surface-navy">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)} className="border-surface-navy text-slate-400">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Founder Profile Section
const FounderSection = () => {
  const [founder, setFounder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/founder`, getAuthHeader())
      .then(res => { setFounder(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/founder`, founder, getAuthHeader());
      toast.success('Founder profile updated');
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPG, JPEG, PNG, WEBP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an image first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const res = await axios.post(`${API}/admin/founder/upload-image`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update founder state with new image
      setFounder(prev => ({ ...prev, image: res.data.image_url }));
      setImagePreview(null);
      setSelectedFile(null);
      toast.success('Image uploaded successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const cancelUpload = () => {
    setImagePreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Note: getImageUrl is now imported from '../utils/imageUtils'

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <h1 className="font-outfit text-2xl font-bold text-white mb-6" data-testid="admin-founder-title">Founder Profile</h1>

      {/* Image Upload Section */}
      <div className="glass-card p-6 mb-6">
        <h2 className="font-outfit text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Image className="w-5 h-5 text-electric-cyan" />
          Founder Image
        </h2>
        
        <div className="flex flex-col md:flex-row gap-6">
          {/* Current Image Preview */}
          <div className="flex-shrink-0">
            <p className="text-sm text-slate-400 mb-2">Current Image</p>
            <div className="w-40 h-40 rounded-xl bg-surface-navy border border-surface-navy overflow-hidden">
              {(imagePreview || founder?.image || founder?.image_url) ? (
                <img
                  src={imagePreview || getImageUrl(founder?.image) || founder?.image_url}
                  alt="Founder"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error('Founder image failed to load:', e.target.src);
                    e.target.onerror = null;
                    // Try fallback to image_url if image fails
                    if (founder?.image_url && e.target.src !== founder.image_url) {
                      e.target.src = founder.image_url;
                    }
                  }}
                  data-testid="founder-image-preview"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-500">
                  <User className="w-16 h-16" />
                </div>
              )}
            </div>
            {imagePreview && (
              <p className="text-xs text-electric-cyan mt-2">Preview - Not saved yet</p>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-1 space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-3">Upload New Image</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                id="founder-image-input"
              />
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-surface-navy text-slate-300 hover:bg-surface-navy"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Select Image
                </Button>

                {selectedFile && (
                  <>
                    <Button
                      type="button"
                      onClick={handleUpload}
                      disabled={uploading}
                      className="btn-primary"
                    >
                      {uploading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-deep-blue mr-2"></div>
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      {uploading ? 'Uploading...' : 'Save Image'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelUpload}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                )}
              </div>
              
              {selectedFile && (
                <p className="text-sm text-slate-400 mt-2">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            <div className="pt-3 border-t border-surface-navy">
              <p className="text-xs text-slate-500">
                Accepted formats: JPG, JPEG, PNG, WEBP • Maximum size: 5MB
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="glass-card p-6 space-y-4">
        <h2 className="font-outfit text-lg font-semibold text-white mb-2">Profile Information</h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Name</label>
            <Input value={founder?.name || ''} onChange={e => setFounder({ ...founder, name: e.target.value })} className="input-dark" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Title</label>
            <Input value={founder?.title || ''} onChange={e => setFounder({ ...founder, title: e.target.value })} className="input-dark" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-2">Credentials</label>
          <Input value={founder?.credentials || ''} onChange={e => setFounder({ ...founder, credentials: e.target.value })} className="input-dark" />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-2">Bio</label>
          <Textarea value={founder?.bio || ''} onChange={e => setFounder({ ...founder, bio: e.target.value })} className="input-dark" rows={5} />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-2">Achievements (one per line)</label>
          <Textarea
            value={founder?.achievements?.join('\n') || ''}
            onChange={e => setFounder({ ...founder, achievements: e.target.value.split('\n').filter(Boolean) })}
            className="input-dark"
            rows={4}
          />
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? 'Saving...' : 'Save Profile Changes'}
        </Button>
      </div>
    </div>
  );
};

// Site Sections
const SiteSectionsSection = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSection, setEditSection] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchSections = () => {
    axios.get(`${API}/admin/site-sections`, getAuthHeader())
      .then(res => { setSections(res.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchSections(); }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/site-sections/${editSection.section_id}`, editSection, getAuthHeader());
      toast.success('Section updated');
      setDialogOpen(false);
      fetchSections();
    } catch {
      toast.error('Update failed');
    }
  };

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <h1 className="font-outfit text-2xl font-bold text-white mb-6" data-testid="admin-sections-title">Site Sections</h1>

      <div className="space-y-4">
        {sections.map(section => (
          <div key={section.section_id} className="glass-card p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-3 h-3 rounded-full ${section.enabled ? 'bg-green-400' : 'bg-slate-500'}`} />
              <div>
                <h3 className="text-white font-medium">{section.title}</h3>
                <p className="text-slate-400 text-sm">{section.section_id}</p>
              </div>
            </div>
            <Button onClick={() => { setEditSection(section); setDialogOpen(true); }} className="btn-secondary" size="sm">
              <Pencil className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-deep-navy border-surface-navy max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Section: {editSection?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <span className="text-slate-400">Enabled</span>
              <Switch checked={editSection?.enabled || false} onCheckedChange={v => setEditSection({ ...editSection, enabled: v })} />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Content (JSON)</label>
              <Textarea
                value={JSON.stringify(editSection?.content || {}, null, 2)}
                onChange={e => {
                  try {
                    setEditSection({ ...editSection, content: JSON.parse(e.target.value) });
                  } catch {}
                }}
                className="input-dark font-mono text-sm"
                rows={15}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleSave} className="btn-primary">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Payment QR Section
const PaymentQRSection = () => {
  const [qrs, setQrs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/payment-qr`, getAuthHeader())
      .then(res => { setQrs(res.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleFileUpload = async (method, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.put(`${API}/admin/payment-qr/${method}`, {
          qr_image_base64: reader.result,
          payment_method: method
        }, getAuthHeader());
        toast.success('QR code updated');
        // Refresh
        const res = await axios.get(`${API}/admin/payment-qr`, getAuthHeader());
        setQrs(res.data || []);
      } catch {
        toast.error('Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <h1 className="font-outfit text-2xl font-bold text-white mb-6" data-testid="admin-qr-title">Payment QR Codes</h1>

      <div className="grid md:grid-cols-2 gap-6">
        {['paynow', 'paylah'].map(method => {
          const qr = qrs.find(q => q.payment_method === method);
          return (
            <div key={method} className="glass-card p-6">
              <h3 className="text-white font-semibold mb-4 capitalize">{method} QR Code</h3>
              <div className="bg-white rounded-xl p-4 mb-4 flex items-center justify-center min-h-[200px]">
                {qr?.qr_image_base64 ? (
                  <img src={qr.qr_image_base64} alt={method} className="max-w-[180px] max-h-[180px] object-contain" />
                ) : (
                  <QrCode className="w-16 h-16 text-slate-300" />
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(method, e)}
                className="hidden"
                id={`qr-upload-${method}`}
              />
              <label htmlFor={`qr-upload-${method}`} className="btn-secondary w-full text-center cursor-pointer flex items-center justify-center gap-2">
                <Image className="w-4 h-4" /> Upload New QR
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/newsletter`, getAuthHeader())
      .then(res => { setSubscribers(res.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <h1 className="font-outfit text-2xl font-bold text-white mb-6" data-testid="admin-newsletter-title">Newsletter Subscribers</h1>
      <div className="glass-card p-4">
        <p className="text-slate-400 mb-4">Total subscribers: {subscribers.length}</p>
        <div className="space-y-2">
          {subscribers.map(sub => (
            <div key={sub.id} className="flex justify-between items-center py-2 border-b border-surface-navy last:border-0">
              <span className="text-white">{sub.email}</span>
              <span className="text-slate-400 text-sm">{new Date(sub.subscribed_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Payment Settings Section (Super Admin Only)
const PaymentSettingsSection = () => {
  const [payNowQr, setPayNowQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    account_name: '',
    bank_name: '',
    account_number: '',
    bank_code: '',
    branch_code: '',
    swift_code: '',
    location: ''
  });
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    // Fetch PayNow QR
    axios.get(`${API}/public/payment-settings/paynow-qr`)
      .then(res => {
        setPayNowQr(res.data.qr_image_url);
      })
      .catch(() => {});

    // Fetch bank details
    axios.get(`${API}/admin/payment-settings/bank-details`, getAuthHeader())
      .then(res => {
        setBankDetails({
          account_name: res.data.account_name || 'PHYSICSCO EXCELLENCE CENTER',
          bank_name: res.data.bank_name || 'DBS Bank Ltd',
          account_number: res.data.account_number || '88537967575',
          bank_code: res.data.bank_code || '7171',
          branch_code: res.data.branch_code || '072',
          swift_code: res.data.swift_code || 'DBSSSGSG',
          location: res.data.location || 'Singapore'
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleBankSave = async () => {
    setSavingBank(true);
    try {
      await axios.put(`${API}/admin/payment-settings/bank-details`, bankDetails, getAuthHeader());
      toast.success('Bank details updated successfully');
    } catch (err) {
      toast.error('Failed to update bank details');
    } finally {
      setSavingBank(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPG, JPEG, PNG, WEBP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an image first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const res = await axios.post(`${API}/admin/payment-settings/upload-paynow-qr`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      setPayNowQr(res.data.qr_image_url);
      setPreviewUrl(null);
      setSelectedFile(null);
      toast.success('PayNow QR code uploaded successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload QR code';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const cancelUpload = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Note: getImageUrl is now imported from '../utils/imageUtils'

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-electric-cyan/20 flex items-center justify-center">
          <Key className="w-5 h-5 text-electric-cyan" />
        </div>
        <div>
          <h1 className="font-outfit text-2xl font-bold text-white" data-testid="admin-payment-settings-title">Payment Settings</h1>
          <p className="text-slate-400 text-sm">Manage payment QR codes and bank details</p>
        </div>
      </div>

      {/* Bank Transfer Details - Editable Form */}
      <div className="glass-card p-6 mb-6">
        <h2 className="font-outfit text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-electric-cyan" />
          Bank Transfer Details
        </h2>
        <p className="text-slate-400 text-sm mb-4">
          These are the bank details displayed to students for manual bank transfers:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Account Name</label>
            <Input
              value={bankDetails.account_name}
              onChange={(e) => setBankDetails({ ...bankDetails, account_name: e.target.value })}
              className="input-dark"
              placeholder="e.g., PHYSICSCO EXCELLENCE CENTER"
              data-testid="bank-account-name"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Bank Name</label>
            <Input
              value={bankDetails.bank_name}
              onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
              className="input-dark"
              placeholder="e.g., DBS Bank Ltd"
              data-testid="bank-bank-name"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Account Number</label>
            <Input
              value={bankDetails.account_number}
              onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })}
              className="input-dark font-mono"
              placeholder="e.g., 88537967575"
              data-testid="bank-account-number"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Bank Code</label>
            <Input
              value={bankDetails.bank_code}
              onChange={(e) => setBankDetails({ ...bankDetails, bank_code: e.target.value })}
              className="input-dark"
              placeholder="e.g., 7171"
              data-testid="bank-bank-code"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Branch Code</label>
            <Input
              value={bankDetails.branch_code}
              onChange={(e) => setBankDetails({ ...bankDetails, branch_code: e.target.value })}
              className="input-dark"
              placeholder="e.g., 072"
              data-testid="bank-branch-code"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">SWIFT Code</label>
            <Input
              value={bankDetails.swift_code}
              onChange={(e) => setBankDetails({ ...bankDetails, swift_code: e.target.value })}
              className="input-dark font-mono"
              placeholder="e.g., DBSSSGSG"
              data-testid="bank-swift-code"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-slate-400 mb-2">Location</label>
            <Input
              value={bankDetails.location}
              onChange={(e) => setBankDetails({ ...bankDetails, location: e.target.value })}
              className="input-dark"
              placeholder="e.g., Singapore"
              data-testid="bank-location"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleBankSave}
            disabled={savingBank}
            className="btn-primary"
            data-testid="bank-save-button"
          >
            {savingBank ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Bank Details
              </>
            )}
          </Button>
        </div>
      </div>

      {/* PayNow QR Code Management */}
      <div className="glass-card p-6">
        <h2 className="font-outfit text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <QrCode className="w-5 h-5 text-electric-cyan" />
          PayNow QR Code
        </h2>
        
        <div className="flex flex-col md:flex-row gap-6">
          {/* Current QR Preview */}
          <div className="flex-shrink-0">
            <p className="text-sm text-slate-400 mb-2">Current QR Code</p>
            <div className="w-48 h-48 rounded-xl bg-white p-4 flex items-center justify-center">
              {(previewUrl || payNowQr) ? (
                <img
                  src={previewUrl || getImageUrl(payNowQr)}
                  alt="PayNow QR Code"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <QrCode className="w-16 h-16 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">No QR uploaded</p>
                </div>
              )}
            </div>
            {previewUrl && (
              <p className="text-xs text-electric-cyan mt-2">Preview - Not saved yet</p>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-1 space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-3">Upload New QR Code</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                id="paynow-qr-input"
              />
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-surface-navy text-slate-300 hover:bg-surface-navy"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Select Image
                </Button>

                {selectedFile && (
                  <>
                    <Button
                      type="button"
                      onClick={handleUpload}
                      disabled={uploading}
                      className="btn-primary"
                    >
                      {uploading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-deep-blue mr-2"></div>
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      {uploading ? 'Uploading...' : 'Save QR Code'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelUpload}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                )}
              </div>
              
              {selectedFile && (
                <p className="text-sm text-slate-400 mt-2">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            <div className="pt-3 border-t border-surface-navy">
              <p className="text-xs text-slate-500">
                Accepted formats: JPG, JPEG, PNG, WEBP • Maximum size: 5MB
              </p>
              <p className="text-xs text-slate-500 mt-1">
                The QR code will be displayed on the payment page for students using PayNow.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Submissions Section
const ContactsSection = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, getAuthHeader())
      .then(res => { setContacts(res.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-electric-cyan"></div></div>;

  return (
    <div>
      <h1 className="font-outfit text-2xl font-bold text-white mb-6" data-testid="admin-contacts-title">Contact Form Submissions</h1>
      <div className="space-y-4">
        {contacts.map(contact => (
          <div key={contact.id} className="glass-card p-4">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-white font-medium">{contact.subject}</h3>
              <span className="text-slate-400 text-xs">{new Date(contact.created_at).toLocaleDateString()}</span>
            </div>
            <p className="text-slate-400 text-sm mb-2">From: {contact.name} ({contact.email})</p>
            <p className="text-slate-300">{contact.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { admin, loading } = useAuth();
  const navigate = useNavigate();

  // Secondary auth check - redirect if not authenticated
  useEffect(() => {
    if (!loading && !admin) {
      // Clear any stale tokens
      try { localStorage.removeItem('adminToken'); } catch(e) {}
      navigate('/admin/login', { replace: true });
    }
  }, [admin, loading, navigate]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-blue flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  // Don't render dashboard if not authenticated
  if (!admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-deep-blue grid-bg flex" data-testid="admin-dashboard-container">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="h-16 border-b border-surface-navy flex items-center px-4 lg:px-6">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white p-2">
            <Menu className="w-6 h-6" />
          </button>
          <span className="text-slate-400 ml-4">Admin Panel</span>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="students" element={<StudentsSection />} />
            <Route path="payment-verifications" element={<PaymentVerificationsSection />} />
            <Route path="courses" element={
              <CrudSection
                title="Courses"
                endpoint="courses"
                fields={[
                  { name: 'name', label: 'Name' },
                  { name: 'description', label: 'Description', type: 'textarea' },
                  { name: 'price', label: 'Price (SGD)', type: 'number' },
                  { name: 'duration', label: 'Duration' },
                  { name: 'grade_levels', label: 'Grade Levels', type: 'array' },
                  { name: 'subjects', label: 'Subjects', type: 'array' },
                  { name: 'features', label: 'Features', type: 'array' },
                  { name: 'is_featured', label: 'Featured', type: 'switch' },
                  { name: 'enabled', label: 'Enabled', type: 'switch', default: true },
                  { name: 'order', label: 'Order', type: 'number' }
                ]}
                displayFields={[
                  { key: 'name', label: 'Name' },
                  { key: 'price', label: 'Price', render: v => `SGD ${v}` },
                  { key: 'is_featured', label: 'Featured', render: v => v ? '⭐' : '-' },
                  { key: 'enabled', label: 'Status', render: v => v ? <span className="text-green-400">Active</span> : <span className="text-slate-400">Disabled</span> }
                ]}
              />
            } />
            <Route path="testimonials" element={
              <CrudSection
                title="Testimonials"
                endpoint="testimonials"
                fields={[
                  { name: 'student_name', label: 'Student Name' },
                  { name: 'school', label: 'School' },
                  { name: 'grade', label: 'Grade' },
                  { name: 'content', label: 'Content', type: 'textarea' },
                  { name: 'rating', label: 'Rating (1-5)', type: 'number', default: 5 },
                  { name: 'enabled', label: 'Enabled', type: 'switch', default: true },
                  { name: 'order', label: 'Order', type: 'number' }
                ]}
                displayFields={[
                  { key: 'student_name', label: 'Student' },
                  { key: 'school', label: 'School' },
                  { key: 'rating', label: 'Rating', render: v => '⭐'.repeat(v || 5) },
                  { key: 'enabled', label: 'Status', render: v => v ? <span className="text-green-400">Active</span> : <span className="text-slate-400">Hidden</span> }
                ]}
              />
            } />
            <Route path="results" element={
              <CrudSection
                title="Results"
                endpoint="results"
                fields={[
                  { name: 'student_name', label: 'Student Name' },
                  { name: 'subject', label: 'Subject' },
                  { name: 'score', label: 'Score' },
                  { name: 'improvement', label: 'Improvement' },
                  { name: 'year', label: 'Year' },
                  { name: 'enabled', label: 'Enabled', type: 'switch', default: true },
                  { name: 'order', label: 'Order', type: 'number' }
                ]}
                displayFields={[
                  { key: 'subject', label: 'Subject' },
                  { key: 'score', label: 'Score', render: v => <span className="text-electric-cyan font-bold">{v}</span> },
                  { key: 'improvement', label: 'Improvement' },
                  { key: 'year', label: 'Year' }
                ]}
              />
            } />
            <Route path="founder" element={<FounderSection />} />
            <Route path="blogs" element={
              <CrudSection
                title="Blog Posts"
                endpoint="blogs"
                fields={[
                  { name: 'title', label: 'Title' },
                  { name: 'slug', label: 'Slug (URL)' },
                  { name: 'excerpt', label: 'Excerpt', type: 'textarea' },
                  { name: 'content', label: 'Content', type: 'textarea' },
                  { name: 'image_url', label: 'Image URL' },
                  { name: 'author', label: 'Author', default: 'Jagalpure Mayur Manoj' },
                  { name: 'tags', label: 'Tags', type: 'array' },
                  { name: 'published', label: 'Published', type: 'switch', default: true }
                ]}
                displayFields={[
                  { key: 'title', label: 'Title' },
                  { key: 'author', label: 'Author' },
                  { key: 'published', label: 'Status', render: v => v ? <span className="text-green-400">Published</span> : <span className="text-yellow-400">Draft</span> }
                ]}
              />
            } />
            <Route path="faqs" element={
              <CrudSection
                title="FAQs"
                endpoint="faqs"
                fields={[
                  { name: 'question', label: 'Question' },
                  { name: 'answer', label: 'Answer', type: 'textarea' },
                  { name: 'category', label: 'Category', default: 'General' },
                  { name: 'enabled', label: 'Enabled', type: 'switch', default: true },
                  { name: 'order', label: 'Order', type: 'number' }
                ]}
                displayFields={[
                  { key: 'question', label: 'Question' },
                  { key: 'category', label: 'Category' },
                  { key: 'enabled', label: 'Status', render: v => v ? <span className="text-green-400">Active</span> : <span className="text-slate-400">Hidden</span> }
                ]}
              />
            } />
            <Route path="sections" element={<SiteSectionsSection />} />
            <Route path="pages" element={
              <CrudSection
                title="Pages"
                endpoint="pages"
                fields={[
                  { name: 'slug', label: 'Slug' },
                  { name: 'title', label: 'Title' },
                  { name: 'meta_description', label: 'Meta Description', type: 'textarea' }
                ]}
                displayFields={[
                  { key: 'slug', label: 'Slug' },
                  { key: 'title', label: 'Title' }
                ]}
              />
            } />
            <Route path="payment-settings" element={<PaymentSettingsSection />} />
            <Route path="newsletter" element={<NewsletterSection />} />
            <Route path="contacts" element={<ContactsSection />} />
            <Route path="complimentary-sessions" element={<ComplimentarySessionsSection />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
